// 省市区工具文件
import regionsData from '@/assets/static/ChinaCitys.json'

/**
 * 获取所有省市区数据（Element UI级联选择器格式）
 * @returns {Array} 格式化后的省市区数据
 */
export function getRegionOptions() {
  return regionsData.map(province => ({
    code: province.code,
    name: province.province,
    children: province.citys.map(city => ({
      code: city.code,
      name: city.city,
      children: city.areas.map(area => ({
        code: area.code,
        name: area.area
      }))
    }))
  }))
}

/**
 * 根据代码获取地区名称
 * @param {string} code 地区代码
 * @returns {string|null} 地区名称，找不到返回null
 */
export function getRegionNameByCode(code) {
  if (!code) return null
  
  const findRegion = (regions, targetCode) => {
    for (const region of regions) {
      if (region.code === targetCode) {
        return region.name
      }
      if (region.children) {
        const found = findRegion(region.children, targetCode)
        if (found) return found
      }
    }
    return null
  }
  
  return findRegion(getRegionOptions(), code)
}

/**
 * 根据代码数组获取地区名称数组
 * @param {Array} codes 地区代码数组
 * @returns {Array} 地区名称数组
 */
export function getRegionNamesByCodes(codes) {
  if (!Array.isArray(codes) || codes.length === 0) {
    return []
  }
  
  return codes.map(code => getRegionNameByCode(code) || code)
}

/**
 * 获取完整的地址字符串
 * @param {Array} regionCodes 省市区代码数组
 * @param {string} detailAddress 详细地址
 * @returns {string} 完整地址
 */
export function getFullAddress(regionCodes, detailAddress = '') {
  if (!Array.isArray(regionCodes) || regionCodes.length === 0) {
    return detailAddress || ''
  }
  
  const regionNames = getRegionNamesByCodes(regionCodes)
  const address = regionNames.join('')
  
  return detailAddress ? address + detailAddress : address
}

/**
 * 获取省份列表
 * @returns {Array} 省份列表
 */
export function getProvinces() {
  return regionsData.map(province => ({
    code: province.code,
    name: province.province
  }))
}

/**
 * 根据省份代码获取城市列表
 * @param {string} provinceCode 省份代码
 * @returns {Array} 城市列表
 */
export function getCitiesByProvince(provinceCode) {
  const province = regionsData.find(p => p.code === provinceCode)
  if (!province) return []
  
  return province.citys.map(city => ({
    code: city.code,
    name: city.city
  }))
}

/**
 * 根据城市代码获取区县列表
 * @param {string} cityCode 城市代码
 * @returns {Array} 区县列表
 */
export function getAreasByCity(cityCode) {
  for (const province of regionsData) {
    for (const city of province.citys) {
      if (city.code === cityCode) {
        return city.areas.map(area => ({
          code: area.code,
          name: area.area
        }))
      }
    }
  }
  return []
}

/**
 * 验证地区代码是否有效
 * @param {string} code 地区代码
 * @returns {boolean} 是否有效
 */
export function isValidRegionCode(code) {
  return getRegionNameByCode(code) !== null
}

/**
 * 获取级联选择器的配置
 * @returns {Object} 级联选择器配置
 */
export function getCascaderProps() {
  return {
    value: 'code',
    label: 'name',
    children: 'children'
  }
}

export default {
  getRegionOptions,
  getRegionNameByCode,
  getRegionNamesByCodes,
  getFullAddress,
  getProvinces,
  getCitiesByProvince,
  getAreasByCity,
  isValidRegionCode,
  getCascaderProps
}
